// Custom hook for OTP page logic
import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { otpSchema } from '@validators/outside.schema';
import { useVerifyOtpMutation, useSendOtpMutation } from './useAuthMutations';

export const useOtp = () => {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const [countdown, setCountdown] = useState(20);
    const verifyMutation = useVerifyOtpMutation();
    const sendOtpMutation = useSendOtpMutation();

    const email = searchParams.get('email');

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleResendOtp = async () => {
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }

        try {
            await sendOtpMutation.mutateAsync(email);
            setCountdown(120);
        } catch (error) {
            // Error handling is done in the mutation
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const onFinish = async (values) => {
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }

        try {
            const parsedValues = otpSchema.parse(values);
            await verifyMutation.mutateAsync({ email, otp: parsedValues.otp });
            navigate('/sign-in');
        } catch (error) {
            if (error instanceof z.ZodError) {
                form.setFields([
                    {
                        name: 'otp',
                        errors: [error.errors[0].message]
                    }
                ]);
            }
        }
    };

    return {
        form,
        loading: verifyMutation.isPending,
        countdown,
        resendLoading: sendOtpMutation.isPending,
        email,
        onFinish,
        handleResendOtp,
        formatTime
    };
};

